// Site create. Required: slug + display_name. Optional: address /
// city / country / geo_lat / geo_lon / edge_host_fqdn + ha_instance_id
// via the HaInstancePicker. Backend slice 1 ensures every optional
// field round-trips into SiteOut so the detail page can render them.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { sites } from "../../api/endpoints/sites";
import { qk } from "../../api/queryKeys";
import { FormShell } from "../../components/forms/FormShell";
import { NumberField } from "../../components/forms/NumberField";
import { TextField } from "../../components/forms/TextField";
import { slugify } from "../../lib/slug";
import type { SiteCreate } from "../../api/types/site";
import type { HaInstanceOut } from "../../api/types/haInstance";

import { HaInstancePicker } from "./HaInstancePicker";

// Established convention: svc-{site-slug}-NN.orixen.internal — see
// control-plane/data/responses/abdullah-khawaja.yml (svc-436-raya-01)
// and platform/.../abdullah_khawaja/khired_onboarding.yml managed_hosts.
// NN=01 is the primary HA host; subsequent services (Frigate, etc.)
// get -02, -03 and are added separately, not auto-derived here.
function deriveEdgeHostFqdn(slug: string): string {
  return slug ? `svc-${slug}-01.orixen.internal` : "";
}

interface SiteCreateFormProps {
  accountSlug: string;
  tenantSlug: string;
  onDone: () => void;
  onPendingChange?: (pending: boolean) => void;
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

interface FormState {
  slug: string;
  display_name: string;
  address: string;
  city: string;
  country: string;
  geo_lat: number | null;
  geo_lon: number | null;
  edge_host_fqdn: string;
  ha_instance: HaInstanceOut | null;
}

const EMPTY: FormState = {
  slug: "",
  display_name: "",
  address: "",
  city: "",
  country: "",
  geo_lat: null,
  geo_lon: null,
  edge_host_fqdn: "",
  ha_instance: null,
};

export function SiteCreateForm({
  accountSlug,
  tenantSlug,
  onDone,
  onPendingChange,
}: SiteCreateFormProps) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<FormState>(EMPTY);
  const slugTouched = useRef(false); // see lib/slug.ts + AccountCreateForm.
  // Same latch pattern: edge_host_fqdn mirrors slug via deriveEdgeHostFqdn
  // until the operator types a custom value. Auto-suggest survives even
  // after slug is manually edited — the FQDN convention is slug-anchored.
  const edgeFqdnTouched = useRef(false);

  const slugError = useMemo(() => {
    if (!values.slug) return null;
    return SLUG_RE.test(values.slug)
      ? null
      : "Slug must match a-z, 0-9, hyphen; 1–63 chars; cannot start with hyphen.";
  }, [values.slug]);

  const mutation = useMutation({
    mutationFn: () => {
      const body: SiteCreate = {
        slug: values.slug,
        display_name: values.display_name,
        address: values.address || null,
        city: values.city || null,
        country: values.country || null,
        geo_lat: values.geo_lat,
        geo_lon: values.geo_lon,
        edge_host_fqdn: values.edge_host_fqdn || null,
        ha_instance_id: values.ha_instance?.id ?? null,
      };
      return sites.create(accountSlug, tenantSlug, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.sites(accountSlug, tenantSlug),
      });
      setValues(EMPTY);
      slugTouched.current = false;
      edgeFqdnTouched.current = false;
      onDone();
    },
  });

  useEffect(() => {
    onPendingChange?.(mutation.isPending);
  }, [mutation.isPending, onPendingChange]);

  const isValid =
    values.slug !== "" &&
    SLUG_RE.test(values.slug) &&
    values.display_name !== "";

  return (
    <FormShell
      submitLabel="Create site"
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid) return;
        mutation.mutate();
      }}
      onCancel={onDone}
      pending={mutation.isPending}
      error={mutation.error}
      errorTitle="Create site failed"
    >
      <TextField
        label="Display name"
        value={values.display_name}
        onChange={(display_name) =>
          setValues((v) => {
            const nextSlug = slugTouched.current ? v.slug : slugify(display_name);
            return {
              ...v,
              display_name,
              slug: nextSlug,
              edge_host_fqdn: edgeFqdnTouched.current
                ? v.edge_host_fqdn
                : deriveEdgeHostFqdn(nextSlug),
            };
          })
        }
        required
        placeholder="Khawaja House"
        helper="The human-readable name. Slug below is auto-derived; edit it if you want something shorter."
      />
      <TextField
        label="Slug"
        value={values.slug}
        onChange={(slug) => {
          slugTouched.current = true;
          setValues((v) => ({
            ...v,
            slug,
            edge_host_fqdn: edgeFqdnTouched.current
              ? v.edge_host_fqdn
              : deriveEdgeHostFqdn(slug),
          }));
        }}
        required
        placeholder="khawaja-house"
        helper="Auto-derived from display name until you edit it. There is no rename — typos stick."
        error={slugError}
      />
      <TextField
        label="Address"
        value={values.address}
        onChange={(address) => setValues((v) => ({ ...v, address }))}
        placeholder="Street + number"
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <TextField
          label="City"
          value={values.city}
          onChange={(city) => setValues((v) => ({ ...v, city }))}
        />
        <TextField
          label="Country"
          value={values.country}
          onChange={(country) => setValues((v) => ({ ...v, country }))}
          placeholder="ISO code or full name"
        />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <NumberField
          label="Latitude"
          value={values.geo_lat}
          onChange={(geo_lat) => setValues((v) => ({ ...v, geo_lat }))}
          step={0.0001}
          min={-90}
          max={90}
          placeholder="31.5497"
        />
        <NumberField
          label="Longitude"
          value={values.geo_lon}
          onChange={(geo_lon) => setValues((v) => ({ ...v, geo_lon }))}
          step={0.0001}
          min={-180}
          max={180}
          placeholder="74.3436"
        />
      </div>
      <TextField
        label="Edge host FQDN"
        value={values.edge_host_fqdn}
        onChange={(edge_host_fqdn) => {
          edgeFqdnTouched.current = true;
          setValues((v) => ({ ...v, edge_host_fqdn }));
        }}
        placeholder="svc-khawaja-house-01.orixen.internal"
        helper="Optional. Auto-derived from slug as svc-{slug}-01.orixen.internal — edit if a different host runs the agent (NN=01 is primary HA; 02+ for Frigate etc., added per service). Resolves over NetBird, not public DNS."
      />
      <HaInstancePicker
        accountSlug={accountSlug}
        tenantSlug={tenantSlug}
        value={values.ha_instance}
        onSelect={(ha_instance) => setValues((v) => ({ ...v, ha_instance }))}
      />
    </FormShell>
  );
}
